import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Spinner,
  Banner,
  EmptyState,
} from '@shopify/polaris';
import { RefreshIcon, PlusIcon } from '@shopify/polaris-icons';
import { useSites, useSyncSites } from '../hooks/useSites';
import { useFeatures } from '../hooks/useFeatures';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/Layout';
import { SiteLimitPrompt } from '../components/UpgradePrompt';
import { WelcomeWizard } from '../components/WelcomeWizard';
import { webflowApi, authApi } from '../lib/api';

export function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: sites, isLoading, error, refetch } = useSites();
  const syncMutation = useSyncSites();
  const { canAddSite, sitesUsed, sitesLimit } = useFeatures();
  const { user, refreshUser } = useAuth();
  const siteLimitError = searchParams.get('error') === 'site_limit_reached';

  const [showWizard, setShowWizard] = useState(!user?.has_seen_welcome);

  const handleDismissWizard = async () => {
    setShowWizard(false);
    try {
      await authApi.welcomeSeen();
      await refreshUser();
    } catch {}
  };

  const handleSync = async () => {
    await syncMutation.mutateAsync();
    refetch();
  };

  const handleAddSite = () => {
    if (!canAddSite) {
      navigate('/pricing');
      return;
    }
    // Redirect to Webflow OAuth to authorize and sync sites
    window.location.href = webflowApi.getAuthorizeUrl();
  };

  const handleSiteClick = (siteId: string, hasGA4: boolean, googleConnected: boolean, isAuthorized: boolean) => {
    // If site is not authorized, redirect to Webflow OAuth
    if (!isAuthorized) {
      window.location.href = webflowApi.getAuthorizeUrl();
      return;
    }

    if (hasGA4 && googleConnected) {
      navigate(`/site/${siteId}/standard-reports`);
    } else if (hasGA4 && !googleConnected) {
      navigate(`/site/${siteId}`); // Go to settings to reconnect Google
    } else {
      navigate(`/site/${siteId}/setup-script`);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <>
    {showWizard && <WelcomeWizard onDismiss={handleDismissWizard} />}
    <Layout>
      <BlockStack gap="400">
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text as="h1" variant="headingLg" fontWeight="bold">
            Sites
          </Text>
          <InlineStack gap="200">
            <Button
              icon={RefreshIcon}
              onClick={handleSync}
              loading={syncMutation.isPending}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={PlusIcon}
              onClick={handleAddSite}
            >
              Add Site
            </Button>
          </InlineStack>
        </div>

        {siteLimitError && (
          <Banner tone="warning" onDismiss={() => {
            searchParams.delete('error');
            setSearchParams(searchParams);
          }}>
            Site limit reached. Upgrade your plan to add more sites.
          </Banner>
        )}

        {!canAddSite && !siteLimitError && (
          <SiteLimitPrompt sitesUsed={sitesUsed} sitesLimit={sitesLimit} />
        )}

        {error && (
          <Banner tone="critical">
            Failed to load sites. Please try again.
          </Banner>
        )}

        {syncMutation.isSuccess && (
          <Banner tone="success" onDismiss={() => syncMutation.reset()}>
            Sites synced successfully!
          </Banner>
        )}

        {sites?.length === 0 ? (
          <Card>
            <EmptyState
              heading="No sites found"
              image="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='160' fill='none'%3E%3Crect x='45' y='25' width='110' height='95' rx='12' fill='%23f0f0f5' stroke='%23c9cccf' stroke-width='2'/%3E%3Crect x='60' y='50' width='80' height='8' rx='4' fill='%23d9dee3'/%3E%3Crect x='60' y='66' width='55' height='8' rx='4' fill='%23d9dee3'/%3E%3Crect x='60' y='82' width='65' height='8' rx='4' fill='%23d9dee3'/%3E%3Ccircle cx='100' cy='38' r='5' fill='%235c6ac4'/%3E%3C/svg%3E"
              action={{
                content: 'Add Site',
                onAction: handleAddSite,
              }}
            >
              <p>
                Click "Add Site" to connect your Webflow account and import your sites.
              </p>
            </EmptyState>
          </Card>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px',
            }}
          >
            {sites?.map((site) => (
              <div
                key={site.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: site.is_authorized ? '1px solid #e1e3e5' : '2px solid #f59e0b',
                  opacity: site.is_authorized ? 1 : 0.9,
                }}
              >
                {/* Site Preview Image */}
                <div
                  style={{
                    height: '180px',
                    backgroundColor: '#f6f6f7',
                    backgroundImage: site.preview_url
                      ? `url(${site.preview_url})`
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Status Badge */}
                  <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                    {!site.is_authorized ? (
                      <span
                        style={{
                          backgroundColor: '#fef3c7',
                          color: '#b45309',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Authorization Required
                      </span>
                    ) : site.has_ga4_property && site.google_connected ? (
                      <span
                        style={{
                          backgroundColor: '#b4fed5',
                          color: '#0d6832',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Connected
                      </span>
                    ) : site.has_ga4_property && !site.google_connected ? (
                      <span
                        style={{
                          backgroundColor: '#ffd79d',
                          color: '#8a6116',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Reconnect Required
                      </span>
                    ) : (
                      <span
                        style={{
                          backgroundColor: '#ffe5b4',
                          color: '#8a6a00',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Setup Required
                      </span>
                    )}
                  </div>
                </div>

                {/* Site Info */}
                <div style={{ padding: '16px' }}>
                  <Text as="h3" variant="headingMd" fontWeight="semibold">
                    {site.display_name}
                  </Text>

                  {site.measurement_id && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {site.measurement_id}
                    </Text>
                  )}

                  {/* Action Buttons */}
                  <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => handleSiteClick(site.id, site.has_ga4_property, site.google_connected, site.is_authorized)}
                      style={{
                        backgroundColor: !site.is_authorized
                          ? '#f59e0b'
                          : site.has_ga4_property && site.google_connected
                            ? '#2c6ecb'
                            : site.has_ga4_property && !site.google_connected
                              ? '#f4a21b'
                              : '#2c6ecb',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = !site.is_authorized
                          ? '#d97706'
                          : site.has_ga4_property && site.google_connected
                            ? '#1f5199'
                            : site.has_ga4_property && !site.google_connected
                              ? '#d68f18'
                              : '#1f5199';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = !site.is_authorized
                          ? '#f59e0b'
                          : site.has_ga4_property && site.google_connected
                            ? '#2c6ecb'
                            : site.has_ga4_property && !site.google_connected
                              ? '#f4a21b'
                              : '#2c6ecb';
                      }}
                    >
                      {!site.is_authorized
                        ? 'Authorize'
                        : site.has_ga4_property && site.google_connected
                          ? 'View Reports'
                          : site.has_ga4_property && !site.google_connected
                            ? 'Reconnect'
                            : 'Setup'}
                    </button>
                    <button
                      onClick={() => site.is_authorized && navigate(`/site/${site.id}`)}
                      disabled={!site.is_authorized}
                      style={{
                        backgroundColor: 'white',
                        color: site.is_authorized ? '#202223' : '#8c9196',
                        border: '1px solid #c9cccf',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: site.is_authorized ? 'pointer' : 'not-allowed',
                        transition: 'background-color 0.15s ease',
                        opacity: site.is_authorized ? 1 : 0.6,
                      }}
                      onMouseEnter={(e) => {
                        if (site.is_authorized) {
                          e.currentTarget.style.backgroundColor = '#f6f6f7';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      Settings
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </BlockStack>
    </Layout>
    </>
  );
}
